import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ORGANIZER_FREE_BENEFITS, PREMIUM_BENEFITS, PRICE_AUDIENCE_NOTE, PROMOTION_BASE_TIER,
  PROMOTION_TIER_SALES, PROMOTION_UPSELL_TIER, PriceItem, formatEuro, netCentsOf, vatNote,
} from '@radevents/shared';
import PublicHeader from '../components/Layout/PublicHeader';
import StaticPageTopbar from '../components/Layout/StaticPageTopbar';
import PremiumBenefitIcon from '../components/organizer/PremiumBenefitIcon';
import OrganizerAccessDialog, { AccessOffer } from '../components/organizer/OrganizerAccessDialog';
import bookingService from '../services/bookingService';
import { organizerFunnelEntry, trackOrganizerFunnel } from '../services/trackingService';
import { SITE_ORIGIN } from '../config/siteOrigin';

// ── Öffentliche Seite „Für Veranstalter" ────────────────────────────────────────────────────
// Der Landeplatz jeder Outreach-Mail (docs/monetarisierung.md, Schritt 6). Bis zum 11.09.2026
// war das Angebot öffentlich unsichtbar: es gab keine Seite mit Preisen, und wer kaufen wollte,
// erfuhr erst nach Konto, Antrag und Freigabe, dass es etwas zu kaufen gibt. Hier stehen die
// drei Dinge, die es gibt — Zugang (kostenlos), Premium, Hervorhebung — mit ihren echten Preisen.
//
// Die Preise kommen aus `GET /api/v1/prices` (`price_items`), nicht aus dem Code: fest
// verdrahtet wären sie beim ersten Preisexperiment falsch, und zwar an der Stelle, an der jemand
// liest. Die Vorteilslisten sind dieselben wie im Veranstalter-Bereich und in der App
// (`shared/src/brand.ts`) — ein Verkaufsversprechen, das je Seite anders lautet, fällt niemandem
// auf, bis sich jemand beschwert.

const FONT_DISPLAY = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const FONT_MONO = "'Space Mono',monospace";
const INK = '#2C2C2C';
const TEXT = '#4A4A4A';
const MUTED = '#6A6A6A';
const BORDER = '#E5E1E1';
const BERRY = '#922C57';

const PAGE_TITLE = 'Für Veranstalter – Events eintragen, Premium, Hervorhebung | Radsport Events';
const PAGE_DESC =
  'Verwalte deine Radsport-Events kostenlos. Mit Premium erinnerst du Interessierte zum richtigen Zeitpunkt; '
  + 'eine Hervorhebung macht ein ausgewähltes Event in den Listen sichtbarer.';
const CANONICAL = `${SITE_ORIGIN}/fuer-veranstalter`;

/** Glatte Beträge ohne Nachkommastellen: „69 €" ist ein Preis, „69,00 €" ein Posten. */
const euroPlain = (cents: number): string =>
  cents % 100 === 0 ? `${(cents / 100).toLocaleString('de-DE')} €` : formatEuro(cents);

/**
 * Was die Seite aus der Preisliste liest.
 *
 * Fehlt ein Preis (abgeschaltet, Liste noch nicht geladen), steht an seiner Stelle „auf Anfrage"
 * — nie eine erfundene Zahl. `null` heißt: die Liste ist noch unterwegs.
 */
export interface OfferPrices {
  club?: number;
  commercial?: number;
  promotion?: number;
  promotionUpsellExtra?: number;
  /** Der Steuersatz, der zu diesen Preisen gehört — für „zzgl. 19 % MwSt." am Preis. */
  vatPercent: number;
}

/**
 * Die Beträge sind **Nettopreise** (V159): so sind sie hinterlegt, und so stehen sie hier. Der
 * Veranstalter ist vorsteuerabzugsberechtigt und vergleicht in netto; der Bruttobetrag kommt im
 * Checkout dazu, wo er gebraucht wird.
 */
export function offerPricesOf(prices: PriceItem[]): OfferPrices {
  const active = prices.filter(p => p.active);
  const net = (p?: PriceItem) => (p ? netCentsOf(p) : undefined);
  const club = active.find(p => p.kind === 'VERIFICATION' && p.audience === 'CLUB');
  const commercial = active.find(p => p.kind === 'VERIFICATION' && p.audience === 'COMMERCIAL');
  const promo = (tier: string) => active
    .filter(p => p.kind === 'PROMOTION' && p.promoTier === tier)
    .sort((a, b) => (a.variant === 'UNTIL_EVENT' ? -1 : 0) - (b.variant === 'UNTIL_EVENT' ? -1 : 0))[0];
  const base = promo(PROMOTION_BASE_TIER);
  const upsell = promo(PROMOTION_UPSELL_TIER);
  const baseNet = net(base);
  const upsellNet = net(upsell);
  return {
    club: net(club),
    commercial: net(commercial),
    promotion: baseNet,
    promotionUpsellExtra: baseNet != null && upsellNet != null && upsellNet > baseNet
      ? upsellNet - baseNet : undefined,
    // Alle Posten tragen denselben Satz; der erste, der da ist, sagt ihn. Ohne Preisliste 19 %,
    // damit der Hinweis am Preis nicht leer bleibt, solange geladen wird.
    vatPercent: (club ?? commercial ?? base ?? upsell)?.vatPercent ?? 19,
  };
}

const CSS = `
.fv-container { flex:1; max-width:1180px; width:100%; margin:0 auto; padding:0 40px 72px; box-sizing:border-box; }
.fv-hero { position:relative; overflow:hidden; margin-top:24px; border-radius:24px;
  background:linear-gradient(115deg,#3A1523 0%,#6E2440 54%,#A8355F 100%); color:#F5F3F3;
  padding:52px 56px; }
.fv-hero-square { position:absolute; right:-40px; top:-80px; width:280px; height:280px;
  background:#C25078; opacity:.16; transform:rotate(45deg); border-radius:56px; }
.fv-eyebrow { font-family:${FONT_MONO}; font-size:12px; letter-spacing:.18em; color:rgba(245,243,243,.7); }
.fv-h1 { font-family:${FONT_DISPLAY}; font-weight:900; font-size:52px; line-height:1.02;
  letter-spacing:-.02em; margin:16px 0 0; max-width:790px; }
.fv-lead { font-family:${FONT_BODY}; font-size:18px; line-height:1.5; color:rgba(245,243,243,.86);
  margin:18px 0 0; max-width:560px; }
.fv-hero-cta { display:inline-flex; align-items:center; gap:10px; margin-top:26px; background:#F5F3F3;
  color:#3A1523; border:none; padding:15px 28px; border-radius:13px; font-family:${FONT_BODY};
  font-weight:700; font-size:16px; text-decoration:none; }
.fv-hero-cta { cursor:pointer; }
.fv-hero-cta:hover { background:#fff; }
.fv-hero-cta--ghost { background:rgba(255,255,255,.12); color:#F5F3F3; border:1px solid rgba(255,255,255,.35); }
.fv-hero-cta--ghost:hover { background:rgba(255,255,255,.2); }

.fv-section-eyebrow { font-family:${FONT_MONO}; font-size:11px; letter-spacing:.14em;
  text-transform:uppercase; color:${BERRY}; margin:48px 0 6px; }
.fv-h2 { font-family:${FONT_DISPLAY}; font-weight:800; font-size:30px; letter-spacing:-.015em;
  color:${INK}; margin:0 0 8px; }
.fv-intro { font-family:${FONT_BODY}; font-size:16px; line-height:1.55; color:${MUTED}; margin:0 0 22px; max-width:640px; }

/* Drei Karten, eine je Sache: Zugang, Premium, Hervorhebung. Gleich hoch, gleich breit — keine
   „empfohlen"-Karte: eine Zielgruppe, ein Kaufmotiv, drei Dinge (docs/monetarisierung.md). */
.fv-plans { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:18px; }
.fv-plan { background:#fff; border:1px solid ${BORDER}; border-radius:14px; padding:26px 26px 24px;
  display:flex; flex-direction:column; min-width:0; }
.fv-plan--dark { background:#241A1F; border-color:#241A1F; color:#F5F3F3; }
/* Die Hervorhebungs-Karte sieht aus wie die hervorgehobene Event-Card: dieselben Werte wie
   .event-card--promo-frame in App.css. Der Rand ist 1px dicker, das Padding 1px kleiner, damit
   die drei Karten bündig bleiben. */
.fv-plan--frame { background:#FFF9FB; border:2px solid ${BERRY}; padding:25px 25px 23px;
  box-shadow:0 10px 30px rgba(146,44,87,0.18); }
.fv-plan-kicker { font-family:${FONT_MONO}; font-size:10.5px; letter-spacing:.14em; text-transform:uppercase;
  color:${MUTED}; }
.fv-plan--dark .fv-plan-kicker { color:#D08BA3; }
.fv-plan-name { font-family:${FONT_DISPLAY}; font-weight:800; font-size:24px; letter-spacing:-.01em;
  color:${INK}; margin:6px 0 0; }
.fv-plan--dark .fv-plan-name { color:#F5F3F3; }
.fv-plan-price { font-family:${FONT_DISPLAY}; font-weight:800; font-size:30px; color:${INK}; margin:14px 0 0;
  letter-spacing:-.02em; }
.fv-plan--dark .fv-plan-price { color:#fff; }
.fv-plan-price small { font-family:${FONT_BODY}; font-weight:400; font-size:14px; color:${MUTED}; margin-left:6px; }
.fv-plan--dark .fv-plan-price small { color:#CFC3C8; }
/* Die Steuerangabe hängt am Preis, nicht am Fließtext darunter: eigene Zeile, damit sie auf
   360px nicht hinter der Bezugsgröße umbricht und dort allein steht. */
.fv-plan-vat { font-family:${FONT_BODY}; font-size:13px; color:${MUTED}; margin:5px 0 0; }
.fv-plan--dark .fv-plan-vat { color:#CFC3C8; }
.fv-plans-note { font-family:${FONT_BODY}; font-size:13.5px; line-height:1.5; color:${MUTED};
  margin:16px 0 0; max-width:640px; }
.fv-plan-sub { font-family:${FONT_BODY}; font-size:13.5px; color:${MUTED}; margin:4px 0 0; line-height:1.5; }
.fv-plan--dark .fv-plan-sub { color:#CFC3C8; }
.fv-plan-list { list-style:none; margin:18px 0 0; padding:18px 0 0; border-top:1px solid ${BORDER};
  display:grid; gap:12px; flex:1; align-content:start; }
.fv-plan--dark .fv-plan-list { border-top-color:rgba(255,255,255,.14); }
.fv-plan-item { display:flex; gap:10px; align-items:flex-start; font-family:${FONT_BODY}; font-size:14.5px;
  line-height:1.45; color:${TEXT}; }
.fv-plan--dark .fv-plan-item { color:#E9E1E4; }
.fv-plan-item strong { display:block; color:${INK}; font-weight:700; }
.fv-plan--dark .fv-plan-item strong { color:#fff; }
.fv-plan-icon { flex-shrink:0; width:22px; height:22px; border-radius:7px; display:flex; align-items:center;
  justify-content:center; color:${BERRY}; background:rgba(146,44,87,.09); margin-top:1px; }
.fv-plan--dark .fv-plan-icon { color:#D08BA3; background:rgba(208,139,163,.16); }
.fv-plan-check { flex-shrink:0; width:22px; height:22px; border-radius:50%; display:flex; align-items:center;
  justify-content:center; color:#fff; background:${BERRY}; margin-top:1px; }
.fv-plan-foot { font-family:${FONT_BODY}; font-size:13px; color:${MUTED}; margin:18px 0 0; line-height:1.5; }
.fv-plan--dark .fv-plan-foot { color:#CFC3C8; }
.fv-plan-cta { margin-top:18px; justify-content:center; width:100%; box-sizing:border-box; }
.fv-plan--dark .fv-plan-cta { background:#F5F3F3; border-color:#F5F3F3; color:#3A1523; }
.fv-plan--dark .fv-plan-cta:hover { background:#fff; }

/* Der Ablauf: drei Schritte nebeneinander, nummeriert in der Monoschrift. */
.fv-steps { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:18px; }
.fv-step { background:#fff; border:1px solid ${BORDER}; border-radius:14px; padding:22px 24px; }
.fv-step-no { font-family:${FONT_MONO}; font-size:11px; letter-spacing:.14em; color:${BERRY}; }
.fv-step strong { display:block; font-family:${FONT_DISPLAY}; font-weight:800; font-size:17px; color:${INK};
  margin:8px 0 6px; }
.fv-step p { font-family:${FONT_BODY}; font-size:14.5px; line-height:1.5; color:${TEXT}; margin:0; }

.fv-faq { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px; }
.fv-faq details { background:#fff; border:1px solid ${BORDER}; border-radius:12px; padding:16px 18px; }
.fv-faq summary { cursor:pointer; list-style:none; font-family:${FONT_DISPLAY}; font-weight:750;
  font-size:15px; color:${INK}; }
.fv-faq summary::-webkit-details-marker { display:none; }
.fv-faq summary::after { content:'+'; float:right; color:${BERRY}; font-family:${FONT_MONO}; }
.fv-faq details[open] summary::after { content:'−'; }
.fv-faq p { font-family:${FONT_BODY}; font-size:14px; line-height:1.55; color:${TEXT}; margin:10px 0 0; }

.fv-cta-row { display:flex; gap:12px; flex-wrap:wrap; margin-top:26px; }
.fv-btn { display:inline-flex; align-items:center; gap:8px; font-family:${FONT_BODY}; font-weight:700;
  font-size:15px; padding:13px 22px; border-radius:11px; text-decoration:none; border:1px solid ${BORDER};
  background:#fff; color:${INK}; }
.fv-btn { cursor:pointer; }
.fv-btn:hover { background:#FBFAFA; }
.fv-btn--primary { background:${BERRY}; border-color:${BERRY}; color:#fff; }
.fv-btn--primary:hover { background:#7E2549; }

@media (max-width: 900px) {
  .fv-plans, .fv-steps, .fv-faq { grid-template-columns:1fr; }
}
@media (max-width: 860px) {
  .fv-container { padding:0 20px 56px; }
  .fv-hero { padding:40px 28px; }
  .fv-h1 { font-size:38px; }
  .fv-h2 { font-size:26px; }
}
`;

const Check: React.FC = () => (
  <span className="fv-plan-check" aria-hidden>
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3"
      strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7" /></svg>
  </span>
);

/**
 * Der Inhalt der Seite, mit den Preisen von außen — so zeigt das Design-Lab ihn mit Beispielpreisen
 * und die Seite mit den echten. `prices === null` heißt: noch unterwegs.
 */
export const FuerVeranstalterContent: React.FC<{
  prices: PriceItem[] | null;
  /** Öffnet den Dialog. Jeder Knopf der Seite führt dorthin, keiner auf eine andere Seite. */
  onStart: (offer: AccessOffer) => void;
}> = ({ prices, onStart }) => {
  const offer = prices ? offerPricesOf(prices) : null;
  /**
   * Preis, Bezugsgröße und Steuerangabe als eine Einheit. Die Steuer steht am Preis und nirgends
   * sonst auf der Seite: sie gehört zur Zahl, nicht in den Fließtext daneben. Bei „0 €" und bei
   * „auf Anfrage" entfällt sie, weil es dort nichts zu versteuern gibt.
   */
  const price = (cents?: number, suffix?: string) => (
    <>
      <div className="fv-plan-price">
        {offer == null ? '…' : cents == null ? 'auf Anfrage' : euroPlain(cents)}
        {offer != null && cents != null && suffix && <small>{suffix}</small>}
      </div>
      {offer != null && cents != null && cents > 0 && (
        <p className="fv-plan-vat">{vatNote(offer.vatPercent)}</p>
      )}
    </>
  );

  return (
    <>
      <style>{CSS}</style>
      <section className="fv-hero">
        <div className="fv-hero-square" />
        <div style={{ position: 'relative' }}>
          <span className="fv-eyebrow">{'// FÜR VERANSTALTER'}</span>
          <h1 className="fv-h1">Mach mehr Radsportler auf dein Event aufmerksam.</h1>
          <p className="fv-lead">
            Pflege deine Veranstalterseite und alle Events kostenlos. Mit Premium erinnerst du
            Interessierte zum Anmeldestart und kurz vor dem Event. Eine Hervorhebung lässt ein
            ausgewähltes Event in unseren Listen stärker auffallen.
          </p>
          <p style={{ fontFamily: FONT_BODY, fontSize: 14, color: 'rgba(245,243,243,.7)', margin: '14px 0 0' }}>
            Rund 60.000 Besucher suchen bei uns jeden Monat nach ihrem nächsten Radsport-Event.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" className="fv-hero-cta" onClick={() => onStart('free')}>
              Kostenlos starten<ArrowRight size={17} aria-hidden />
            </button>
            <a className="fv-hero-cta fv-hero-cta--ghost" href="#angebote">Angebote und Preise ansehen</a>
          </div>
        </div>
      </section>

      <h2 id="angebote" className="fv-h2" style={{ marginTop: 48, marginBottom: 22 }}>
        Wähle, was du gerade brauchst.
      </h2>

      <div className="fv-plans">
        <article className="fv-plan" aria-labelledby="fv-plan-basis">
          <div className="fv-plan-kicker">Events selbst verwalten</div>
          <h3 id="fv-plan-basis" className="fv-plan-name">Kostenlos verwalten</h3>
          <div className="fv-plan-price">0 €<small>dauerhaft</small></div>
          {/* Keine Steuerzeile: an 0 € ist nichts zu versteuern, und eine leere Zeile nur der
              Bündigkeit wegen wäre eine Angabe, die nichts sagt. */}
          <p className="fv-plan-sub">Halte deine Veranstalterseite und alle Events selbst aktuell.</p>
          <ul className="fv-plan-list">
            {ORGANIZER_FREE_BENEFITS.map(b => (
              <li className="fv-plan-item" key={b.title}>
                <Check />
                <span><strong>{b.title}</strong>{b.text}</span>
              </li>
            ))}
          </ul>
          <p className="fv-plan-foot">
            Mit einer bestätigten E-Mail-Adresse deines Vereins erhältst du sofort Zugang.
            Andernfalls prüfen wir deine Anfrage kurz.
          </p>
          <button type="button" className="fv-btn fv-plan-cta" onClick={() => onStart('free')}>
            Kostenlos verwalten
          </button>
        </article>

        <article className="fv-plan fv-plan--dark" aria-labelledby="fv-plan-premium">
          <div className="fv-plan-kicker">Interessierte informieren</div>
          <h3 id="fv-plan-premium" className="fv-plan-name">Premium</h3>
          {price(offer?.club, 'pro Jahr für Vereine')}
          <p className="fv-plan-sub">
            {offer?.commercial != null
              ? `Gewerbliche Veranstalter ${euroPlain(offer.commercial)} pro Jahr. Keine automatische Verlängerung.`
              : 'Keine automatische Verlängerung.'}
          </p>
          <ul className="fv-plan-list">
            {PREMIUM_BENEFITS.map(b => (
              <li className="fv-plan-item" key={b.title}>
                <span className="fv-plan-icon"><PremiumBenefitIcon name={b.icon} /></span>
                <span><strong>{b.title}</strong>{b.text}</span>
              </li>
            ))}
          </ul>
          <button type="button" className="fv-btn fv-plan-cta" onClick={() => onStart('premium')}>
            Premium aktivieren
          </button>
        </article>

        <article className="fv-plan fv-plan--frame" aria-labelledby="fv-plan-promo">
          <div className="fv-plan-kicker">Ein Event sichtbarer machen</div>
          <h3 id="fv-plan-promo" className="fv-plan-name">Hervorhebung</h3>
          {price(offer?.promotion, 'pro Event')}
          <p className="fv-plan-sub">
            Dein Event erhält einen auffälligen Rahmen und hebt sich in den Veranstaltungslisten
            von normalen Einträgen ab.
          </p>
          <ul className="fv-plan-list">
            <li className="fv-plan-item">
              <Check />
              <span>
                <strong>Enthalten: {PROMOTION_TIER_SALES[PROMOTION_BASE_TIER].name}</strong>
                {PROMOTION_TIER_SALES[PROMOTION_BASE_TIER].benefit}
              </span>
            </li>
            {offer?.promotionUpsellExtra != null && (
              <li className="fv-plan-item">
                <Check />
                <span>
                  <strong>Optional: {PROMOTION_TIER_SALES[PROMOTION_UPSELL_TIER].name} (+{euroPlain(offer.promotionUpsellExtra)})</strong>
                  {PROMOTION_TIER_SALES[PROMOTION_UPSELL_TIER].benefit}
                </span>
              </li>
            )}
          </ul>
          <p className="fv-plan-foot">
            Sichtbar bis zum Tag nach dem Event. Mit Premium 20 % günstiger.
          </p>
          <button type="button" className="fv-btn fv-plan-cta" onClick={() => onStart('promotion')}>
            Event hervorheben
          </button>
        </article>
      </div>

      {/* Für wen die Nettopreise gelten. Steht sichtbar unter den Karten und nicht im
          Kleingedruckten: ein Verein, der 69 € liest und 82,11 € überweisen soll, erfährt es
          sonst erst im Checkout. Der Satz steht einmal, in `shared/src/brand.ts`. */}
      <p className="fv-plans-note">{PRICE_AUDIENCE_NOTE}</p>

      <div className="fv-section-eyebrow">So läuft es</div>
      <h2 className="fv-h2">In wenigen Minuten startklar</h2>
      <div className="fv-steps">
        <div className="fv-step">
          <div className="fv-step-no">01</div>
          <strong>Veranstalter finden</strong>
          <p>
            Die meisten Vereine und Anbieter stehen schon bei uns, mit ihren Events. Such deinen
            und übernimm die Seite. Fehlt er, trägst du ihn im selben Schritt selbst ein.
          </p>
        </div>
        <div className="fv-step">
          <div className="fv-step-no">02</div>
          <strong>E-Mail angeben</strong>
          <p>
            Wir schicken dir einen Code, damit war es das mit der Anmeldung. Passt deine Adresse
            zur Domain des Veranstalters, ist der Zugang sofort da. Sonst prüfen wir kurz.
          </p>
        </div>
        <div className="fv-step">
          <div className="fv-step-no">03</div>
          <strong>Kostenlos verwalten oder Angebot wählen</strong>
          <p>
            Pflege deine Events dauerhaft kostenlos. Premium gilt für alle deine Events;
            Hervorhebungen buchst du gezielt für eine Veranstaltung.
          </p>
        </div>
      </div>

      <div className="fv-section-eyebrow">Häufige Fragen</div>
      <h2 className="fv-h2">Gut zu wissen</h2>
      <div className="fv-faq">
        <details>
          <summary>Verlängert sich Premium automatisch?</summary>
          <p>Nein. Premium läuft zwölf Monate und endet ohne automatische Verlängerung.</p>
        </details>
        <details>
          <summary>Kann ich meine Events ohne Premium bearbeiten?</summary>
          <p>Ja. Veranstalterseite, Logo, Events, Fotos und Flyer bleiben dauerhaft kostenlos.</p>
        </details>
        <details>
          <summary>Wie bezahle ich?</summary>
          <p>
            Nach der Bestellung erhältst du die Zahlungsdaten und überweist innerhalb von
            14 Tagen. Zu überweisen ist der Bruttobetrag, den dir die Bestellbestätigung nennt.
            Nach Zahlungseingang bekommst du die Rechnung mit ausgewiesener Umsatzsteuer.
          </p>
        </details>
        <details>
          <summary>Brauche ich Premium für eine Hervorhebung?</summary>
          <p>Nein. Du kannst jedes deiner Events einzeln hervorheben. Mit Premium ist es 20 % günstiger.</p>
        </details>
      </div>

      <div className="fv-cta-row">
        <button type="button" className="fv-btn fv-btn--primary" onClick={() => onStart('free')}>
          Deinen Veranstalter finden
        </button>
        <Link className="fv-btn" to="/kontakt">Fragen? Schreib uns</Link>
      </div>
    </>
  );
};

const FuerVeranstalter: React.FC = () => {
  const [prices, setPrices] = useState<PriceItem[] | null>(null);
  /**
   * Der gewählte Weg, und damit auch: ist der Dialog offen?
   *
   * Der ganze Ablauf bis zum Zugang läuft hier auf der Seite (siehe `OrganizerAccessDialog`).
   * Vorher führte jeder Knopf auf die Veranstalterliste und von dort über drei weitere Seiten.
   */
  const [offer, setOffer] = useState<AccessOffer | null>(null);
  const [params] = useSearchParams();
  // `?via=banner` hängt der Banner auf der Startseite an; so steht in den Geschäftszahlen, wie
  // viele Besucher der Angebotsseite er bringt.
  const entry = organizerFunnelEntry(params.get('via'));

  useEffect(() => {
    trackOrganizerFunnel('offer_viewed', { entry }, true);
    let alive = true;
    bookingService.publicPrices()
      .then(list => { if (alive) setPrices(list); })
      // Ohne Liste stehen die Preise als „auf Anfrage" — nie als erfundene Zahl.
      .catch(() => { if (alive) setPrices([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- einmal je Seitenaufruf, der Eingang steht beim ersten Render fest
  }, []);

  const startAccess = (chosen: AccessOffer) => {
    trackOrganizerFunnel('offer_selected', { offer: chosen });
    setOffer(chosen);
  };

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESC} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESC} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESC} />
      </Helmet>

      <div className="hide-mobile">
        <PublicHeader />
      </div>
      <StaticPageTopbar label="// FÜR VERANSTALTER" />

      <div className="fv-container">
        <FuerVeranstalterContent prices={prices} onStart={startAccess} />
      </div>


      <OrganizerAccessDialog show={offer != null} offer={offer ?? undefined} onClose={() => setOffer(null)} />
    </div>
  );
};

export default FuerVeranstalter;
